// Decisão do bot Normal sobre atacar o boss.
import { hasTurnEffect, lairAdjacent, neighborsOf } from '../selectors';
import type { Action, PlayerView, TerritoryId } from '../types';

type BossAttack = Extract<Action, { type: 'attackBoss' }>;

const cache = new Map<string, { damage: number; loss: number }>();

/**
 * Média por rolagem de `n` dados de ataque contra `m` dados do boss: vida tirada
 * (`damage`) e tropas perdidas (`loss`). Conta exata sobre todas as combinações.
 */
export function bossRollOdds(n: number, m: number, tieToAttacker = false): { damage: number; loss: number } {
  const key = `${n}-${m}-${tieToAttacker}`;
  const hit = cache.get(key);
  if (hit) return hit;
  let damage = 0;
  let loss = 0;
  let total = 0;
  const faces = [1, 2, 3, 4, 5, 6];
  const rolls = (k: number): number[][] => (k === 0 ? [[]] : rolls(k - 1).flatMap((r) => faces.map((f) => [...r, f])));
  for (const a of rolls(n)) {
    for (const d of rolls(m)) {
      const as = [...a].sort((x, y) => y - x);
      const ds = [...d].sort((x, y) => y - x);
      for (let i = 0; i < Math.min(n, m); i++) {
        if (as[i] > ds[i] || (as[i] === ds[i] && tieToAttacker)) damage++;
        else loss++;
      }
      total++;
    }
  }
  const out = { damage: damage / total, loss: loss / total };
  cache.set(key, out);
  return out;
}

/** Inimigo vizinho mais forte (0 se o território não é de fronteira). */
function strongestEnemy(v: PlayerView, t: TerritoryId): number {
  const me = v.me!;
  let max = 0;
  for (const n of neighborsOf(v, t)) if (v.territories[n].owner !== me) max = Math.max(max, v.territories[n].troops);
  return max;
}

/** Territórios que a raid do boss atingiria. */
function raidTargets(v: PlayerView): TerritoryId[] {
  const boss = v.boss!;
  const adj = lairAdjacent(v, boss.region);
  if (boss.raid.type !== 'quake') return adj;
  const ring = new Set(adj);
  for (const t of adj) for (const n of neighborsOf(v, t)) ring.add(n);
  return [...ring];
}

/**
 * Ataca o boss se pode dar o último golpe neste turno, se fecha um par de dano
 * (ganha carta de território) ou se a raid vai atingir um território seu. Em
 * qualquer caso, só se a origem continuar pelo menos tão forte quanto o inimigo
 * vizinho mais forte depois da rolagem.
 */
export function bossAttackChoice(v: PlayerView, attacks: BossAttack[]): BossAttack | null {
  const boss = v.boss;
  if (!boss || !attacks.length) return null;
  const me = v.me!;
  const per = v.info.ruleset.bossDamagePerCard;
  const tie = hasTurnEffect(v, me, 'precision');
  let best: { a: BossAttack; score: number } | null = null;
  for (const a of attacks) {
    const troops = v.territories[a.from].troops;
    const n = Math.min(v.info.ruleset.maxAttackDice, troops - 1);
    const { damage, loss } = bossRollOdds(n, boss.defenseDice, tie);
    const floor = Math.max(1, strongestEnemy(v, a.from));
    // segurança: depois desta rolagem, a origem não fica abaixo do inimigo mais forte ao lado
    if (troops - loss < floor || troops - Math.ceil(loss) < 2) continue;
    // rolagens que dá para bancar mantendo essa reserva
    const rolls = Math.floor((troops - floor - 1) / Math.max(loss, 0.3));
    const expected = rolls * damage;
    const lastHit = expected >= boss.hp;
    const mine = boss.damage[me] ?? 0;
    const pair = per > 0 && (mine % per) + expected >= per;
    const raid = raidTargets(v).some((t) => v.territories[t].owner === me);
    if (!lastHit && !pair && !raid) continue;
    const score = (lastHit ? 3 : 0) + (pair ? 1 : 0) + (raid ? 1 : 0) + troops * 0.01;
    if (!best || score > best.score) best = { a, score };
  }
  return best?.a ?? null;
}
